import type { ReactNode } from 'react';

// Realce de sintaxe mínimo para os exemplos da documentação da API (sem
// dependência): cada linguagem é uma regex com um grupo por tipo de token.

export type Linguagem = 'json' | 'bash' | 'js' | 'python' | 'http' | 'texto';

type Tipo = 'chave' | 'texto' | 'numero' | 'literal' | 'comentario' | 'palavra' | 'funcao' | 'variavel' | 'opcao';

const COR: Record<Tipo, string> = {
  chave: 'text-[hsl(199,89%,74%)]',
  texto: 'text-[hsl(142,55%,70%)]',
  numero: 'text-[hsl(43,80%,68%)]',
  literal: 'text-[hsl(270,80%,80%)]',
  comentario: 'italic text-[hsl(215,16%,55%)]',
  palavra: 'text-[hsl(330,80%,76%)]',
  funcao: 'text-[hsl(199,95%,82%)]',
  variavel: 'text-[hsl(25,95%,72%)]',
  opcao: 'text-[hsl(199,89%,74%)]',
};

// Ordem dos grupos = ordem dos tipos em REGRAS[l].tipos.
const REGRAS: Record<Exclude<Linguagem, 'texto'>, { regex: RegExp; tipos: Tipo[] }> = {
  json: {
    regex: /("(?:\\.|[^"\\])*")(?=\s*:)|("(?:\\.|[^"\\])*")|(-?\b\d+(?:\.\d+)?\b)|\b(true|false|null)\b/g,
    tipos: ['chave', 'texto', 'numero', 'literal'],
  },
  bash: {
    regex: /(#.*)|('[^']*'|"(?:\\.|[^"\\])*")|(\$\w+)|((?:^|\s)--?[A-Za-z][\w-]*)|(\bcurl\b)/gm,
    tipos: ['comentario', 'texto', 'variavel', 'opcao', 'funcao'],
  },
  js: {
    regex:
      /(\/\/.*)|('(?:\\.|[^'\\])*'|"(?:\\.|[^"\\])*"|`[^`]*`)|\b(const|let|function|return|require|new|if|else)\b|\b(true|false|null|undefined)\b|(\b\d+(?:\.\d+)?\b)|(\b[A-Za-z_]\w*(?=\())/g,
    tipos: ['comentario', 'texto', 'palavra', 'literal', 'numero', 'funcao'],
  },
  python: {
    regex:
      /(#.*)|(b?"(?:\\.|[^"\\])*"|b?'(?:\\.|[^'\\])*')|\b(import|from|def|return|and|or|not|if|else)\b|\b(True|False|None)\b|(\b\d+(?:\.\d+)?\b)|(\b[A-Za-z_]\w*(?=\())/g,
    tipos: ['comentario', 'texto', 'palavra', 'literal', 'numero', 'funcao'],
  },
  http: {
    regex: /(^[\w-]+(?=:))|(<[^>]+>)/gm,
    tipos: ['chave', 'variavel'],
  },
};

export function realcar(codigo: string, linguagem: Linguagem): ReactNode {
  if (linguagem === 'texto') return codigo;
  const { regex, tipos } = REGRAS[linguagem];
  const partes: ReactNode[] = [];
  let fim = 0;
  for (const m of Array.from(codigo.matchAll(regex))) {
    const grupo = m.slice(1).findIndex((g) => g !== undefined);
    if (grupo < 0 || m[0] === '') continue;
    if (m.index! > fim) partes.push(codigo.slice(fim, m.index));
    partes.push(
      <span key={m.index} className={COR[tipos[grupo]]}>
        {m[0]}
      </span>
    );
    fim = m.index! + m[0].length;
  }
  partes.push(codigo.slice(fim));
  return partes;
}
